import { useMemo, useState, type FormEvent } from 'react';
import type { RolEvento } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { apiAuth, ApiError } from '../lib/apiAuth';
import { useAdminEventoData } from './AdminEventoDataProvider';

interface RespLote {
  creadas: number;
  aforo: { objetivo: number; comprometido: number; disponible: number };
  advertencia: string | null;
}

export function EquipoScreen() {
  const { eventoId, usuarios, miembros, invitaciones } = useAdminEventoData();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tono: 'exito' | 'alerta' | 'error'; texto: string } | null>(null);

  const asesoresDelEvento = useMemo(
    () =>
      miembros
        .filter((m) => m.activo && m.roles.includes('asesor'))
        .map((m) => usuarios.find((u) => u.uid === m.uid))
        .filter((u): u is NonNullable<typeof u> => Boolean(u)),
    [miembros, usuarios],
  );

  const porAsesor = useMemo(() => {
    const m = new Map<string, { porEnviar: number; enviadas: number; registros: number }>();
    for (const i of invitaciones) {
      const e = m.get(i.asesorUid) ?? { porEnviar: 0, enviadas: 0, registros: 0 };
      if (i.estado === 'disponible' || i.estado === 'en_mano') e.porEnviar += 1;
      if (i.estado === 'entregada' || i.estado === 'registrada') e.enviadas += 1;
      if (i.estado === 'registrada') e.registros += 1;
      m.set(i.asesorUid, e);
    }
    return m;
  }, [invitaciones]);

  async function asignarRapido(asesorUid: string, cupo: 1 | 2) {
    setOcupado(`${asesorUid}-${cupo}`);
    setMsg(null);
    try {
      const r = await apiAuth<RespLote>(`/admin/eventos/${eventoId}/lote`, {
        method: 'POST',
        body: JSON.stringify({ asesorUid, cupo, cantidad: 1 }),
      });
      setMsg({
        tono: r.advertencia ? 'alerta' : 'exito',
        texto: r.advertencia ?? `Invitación ${cupo === 2 ? 'de pareja' : 'individual'} asignada.`,
      });
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo asignar' });
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-texto">Asesores del evento</h2>

        {asesoresDelEvento.length === 0 ? (
          <Card className="p-4 text-sm text-texto/50">
            Todavía no hay asesores vinculados a este evento — vincula uno abajo.
          </Card>
        ) : (
          <ul className="space-y-2">
            {asesoresDelEvento.map((a) => {
              const c = porAsesor.get(a.uid) ?? { porEnviar: 0, enviadas: 0, registros: 0 };
              return (
                <li key={a.uid}>
                  <Card className="space-y-3 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-sm font-medium text-texto">{a.nombre}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <Cifra n={c.porEnviar} etiqueta="por enviar" />
                      <Cifra n={c.enviadas} etiqueta="enviadas" />
                      <Cifra n={c.registros} etiqueta="registros" />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variante="secundario"
                        cargando={ocupado === `${a.uid}-1`}
                        onClick={() => asignarRapido(a.uid, 1)}
                      >
                        +1 individual
                      </Button>
                      <Button
                        variante="secundario"
                        cargando={ocupado === `${a.uid}-2`}
                        onClick={() => asignarRapido(a.uid, 2)}
                      >
                        +1 pareja
                      </Button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        {msg && <Callout tono={msg.tono}>{msg.texto}</Callout>}
      </div>

      <VincularMiembro eventoId={eventoId} />
    </div>
  );
}

function Cifra({ n, etiqueta }: { n: number; etiqueta: string }) {
  return (
    <div className="rounded-lg bg-navy/5 px-2 py-2">
      <span className="block text-base font-semibold text-texto">{n}</span>
      <span className="text-xs text-texto/50">{etiqueta}</span>
    </div>
  );
}

function VincularMiembro({ eventoId }: { eventoId: string }) {
  const { usuarios, miembros } = useAdminEventoData();
  const [rol, setRol] = useState<RolEvento>('asesor');

  const disponibles = useMemo(() => {
    const yaVinculados = new Set(miembros.filter((m) => m.activo && m.roles.includes(rol)).map((m) => m.uid));
    return usuarios.filter((u) => u.activo && u.roles.includes(rol) && !yaVinculados.has(u.uid));
  }, [usuarios, miembros, rol]);

  const [uid, setUid] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ tono: 'exito' | 'error'; texto: string } | null>(null);

  async function vincular(e: FormEvent) {
    e.preventDefault();
    if (!uid) return;
    setMsg(null);
    setEnviando(true);
    try {
      const existente = miembros.find((m) => m.uid === uid);
      const roles = Array.from(new Set([...(existente?.roles ?? []), rol]));
      await apiAuth(`/admin/eventos/${eventoId}/miembros/${uid}`, {
        method: 'PATCH',
        body: JSON.stringify({ roles, activo: true }),
      });
      setMsg({ tono: 'exito', texto: `${rol === 'asesor' ? 'Asesor' : 'Staff'} vinculado a este evento.` });
      setUid('');
    } catch (err) {
      setMsg({ tono: 'error', texto: err instanceof ApiError ? err.message : 'No se pudo vincular' });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Card className="border-navy/15 bg-navy/3 p-5">
      <form className="flex flex-col gap-4" onSubmit={vincular}>
        <div>
          <h2 className="text-sm font-semibold text-texto">Dar de alta o asociar al evento</h2>
          <p className="mt-1 text-xs text-texto/50">
            Solo aparecen usuarios que ya tienen esa capacidad global. Para crear un usuario nuevo, ve a
            Usuarios. Para staff de puerta sin cuenta, usa la pestaña Códigos.
          </p>
        </div>

        <div className="flex gap-2">
          <Chip activo={rol === 'asesor'} onClick={() => setRol('asesor')}>
            Asesor
          </Chip>
          <Chip activo={rol === 'staff'} onClick={() => setRol('staff')}>
            Staff
          </Chip>
        </div>

        <select
          className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
          value={uid}
          onChange={(e) => setUid(e.target.value)}
        >
          <option value="">Elige un usuario…</option>
          {disponibles.map((u) => (
            <option key={u.uid} value={u.uid}>
              {u.nombre} ({u.email})
            </option>
          ))}
        </select>
        {msg && <Callout tono={msg.tono}>{msg.texto}</Callout>}
        <Button type="submit" variante="secundario" cargando={enviando} disabled={!uid}>
          Vincular
        </Button>
      </form>
    </Card>
  );
}
